import type { SelectOption } from '@kula-ai/components';
import { participantsToOptions } from '@/api/userSearch/useCcBccUserOptions';

/** Email configs a plan (create) or existing interview (edit) can carry. */
const EMAIL_CONFIG_KEYS = [
  'candidate_email_config',
  'interviewer_email_config',
  'candidate_self_schedule_email_config',
  'candidate_availability_email_config',
] as const;

const configsOf = (plan: Record<string, unknown> | undefined) =>
  plan
    ? EMAIL_CONFIG_KEYS.map(
        (key) => plan[key] as Record<string, unknown> | undefined
      )
    : [];

/**
 * Saved cc/bcc participants read straight off the raw plan.
 *
 * The wizard's initial values keep only recipient ids, so a saved recipient
 * outside the first page of the user search had nothing to label itself with
 * and rendered as no chip at all (ENG-3766). The raw plan still carries the
 * full participant objects, so seed the pickers from there.
 */
export const planRecipientOptions = (
  plan: Record<string, unknown> | undefined
): SelectOption[] => [
  ...configsOf(plan).flatMap((cfg) => [
    ...participantsToOptions(cfg?.cc_participants),
    ...participantsToOptions(cfg?.bcc_participants),
  ]),
  // Edit mode puts the slot-selection email's recipients at the top level
  // under `candidate_mail_*` rather than in a `*_config`.
  ...participantsToOptions(plan?.candidate_mail_cc_participants),
  ...participantsToOptions(plan?.candidate_mail_bcc_participants),
];

/** Saved senders off the same configs, for the From picker. */
export const planSenderOptions = (
  plan: Record<string, unknown> | undefined
): SelectOption[] => [
  ...configsOf(plan).flatMap((cfg) => participantsToOptions([cfg?.sender])),
  ...participantsToOptions([plan?.candidate_mail_from]),
];
